<template>
  <div ref="blockRef" class="mx-auto px-4 max-w-6xl">
    <div class="max-w-[41.5rem]">
      <h2
        v-if="block.title"
        v-html="formatTitle(block.title)"
        :class="`text-[clamp(1.5rem,-2.25rem+18.75vw,3.75rem)] leading-none ${block.title_color?.value}`"
        data-gsap-target="title"
      />

      <div
        v-if="block.text"
        v-html="block.text"
        class="prose mt-4 md:mt-8"
        data-gsap-target="text"
      />
    </div>

    <div
      v-if="employees?.length"
      class="grid grid-cols-2 gap-6 mt-8 sm:grid-cols-3 md:gap-12 md:mt-12 lg:gap-[4.75rem] lg:mt-24"
    >
      <NuxtLink
        v-for="employee in employees"
        :key="employee.id"
        :to="employee.url"
        data-gsap-target="employee"
      >
        <div
          v-if="employee.image"
          :class="`relative overflow-hidden rounded-3xl aspect-[20/23] bg-gradient-employee`"
        >
          <img
            :src="employee.image.xs.url"
            :srcSet="`
              ${employee.image.md.url} ${employee.image.md.width}w,
              ${employee.image.sm.url} ${employee.image.sm.width}w,
              ${employee.image.xs.url} ${employee.image.xs.width}w
            `"
            :alt="employee.image.md.alt"
            class="h-full w-full object-cover"
            sizes="
              (min-media: 768px) 30vw,
              (min-media: 640px) 45vw,
              95vw
            "
            :width="employee.image.xs.width"
            :height="employee.image.xs.height"
            loading="lazy"
            draggable="false"
          >
        </div>

        <div
          v-if="employee.title"
          class="mt-4 text-xl font-serif font-bold text-purple-dark lg:text-2xl"
        >
          {{ employee.title }}
        </div>

        <div
          v-if="employee.position"
          class="text-xs font-light text-purple-dark"
        >
          {{ employee.position }}
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Employees } from '~/statamic/types/block';

defineProps<{
  block: Employees;
}>();

const { data: employees } = await useStatamicEntries({ collection: 'employees', sort: 'order' });

let ctx: gsap.Context | null;
const blockRef = ref();

onMounted(() => {
  ctx = useGsap.context((self) => {
    if (typeof self.selector !== 'function') return;

    const titleLeft = self.selector('[data-gsap-target="title"]')[0];
    const titleRight = self.selector('[data-gsap-target="title"] > .text-right')[0];
    const text = self.selector('[data-gsap-target="text"]')[0];
    const employeesGsap = self.selector('[data-gsap-target="employee"]');

    const timeline = useGsap.timeline({
      scrollTrigger: {
        start: 'top+=100 bottom',
        trigger: blockRef.value,
      },
    });

    if (titleLeft) {
      timeline.from(titleLeft, {
        x: -100,
        autoAlpha: 0,
      });
    }

    if (titleRight) {
      timeline.from(titleRight, {
        x: 100,
        autoAlpha: 0,
      }, '-=0.25');
    }

    if (text) {
      timeline.from(text, {
        y: 10,
        scale: 0.5,
        stagger: 1.0,
        autoAlpha: 0,
      }, '0');
    }

    if (employeesGsap?.length) {
      timeline.from(employeesGsap, {
        y: 50,
        stagger: 0.25,
        autoAlpha: 0,
      }, '0');
    }
  }, blockRef.value);
});

onUnmounted(() => {
  ctx?.revert();
});
</script>
